/* ========================
 * SnippetsManager.jsx - Manages snippet data, state, and Firestore interactions
 * Simple text-based search and filtering only
 * ======================== */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { db } from '@/firebase/config';
import { collection, doc, setDoc, deleteDoc, query, onSnapshot } from 'firebase/firestore';
import { authManager } from '@/managers/AuthManager';
import { CryptoUtils } from '@/utils/cryptoUtils';
import debounce from 'lodash/debounce';

const DEBOUNCE_MS = 1000;

/* ========================
 * safeDecrypt - Safely decrypts data, returning original on error
 * ======================== */
const safeDecrypt = async (data, userId) => {
    if (!data) return '';
    try {
        const decrypted = await CryptoUtils.decrypt(data, userId);
        return String(decrypted);
    } catch (error) {
        // Error state will be handled by calling function if necessary
        return String(data); // Return original encrypted data as string if decryption fails
    }
};

/* ========================
 * safeJSONParse - Safely parses JSON, returning a fallback on error
 * ======================== */
const safeJSONParse = (str, fallback = []) => {
    if (!str) return fallback;
    if (Array.isArray(str)) return str;
    try {
        const parsed = JSON.parse(str);
        return Array.isArray(parsed) ? parsed : fallback;
    } catch (error) {
        return fallback;
    }
};

const SnippetsContext = createContext();

/* ========================
 * useSnippets - Hook to access SnippetsContext
 * ======================== */
export const useSnippets = () => {
    const context = useContext(SnippetsContext);
    if (!context) throw new Error('useSnippets must be used within SnippetsManager');
    return context;
};

/* ========================
 * SnippetsManager - Provider component for snippet management with text search
 * ======================== */
export const SnippetsManager = ({ children }) => {
    const { user } = authManager.getState();
    const [snippets, setSnippets] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const pendingUpdates = useRef(new Map());
    const unsubscribeRef = useRef(null);

    /* ========================
     * debouncedSave - Debounces snippet save operations to Firestore
     * ======================== */
    const debouncedSave = useCallback(
        debounce(async (snippetId, updates) => {
            if (!user?.uid) return;

            try {
                const snippetsRef = collection(db, `users/${user.uid}/snippets`);
                const snippetRef = doc(snippetsRef, snippetId);
                
                const encryptedUpdates = {};

                if (updates.code !== undefined) {
                    encryptedUpdates.code = await CryptoUtils.encrypt(
                        String(updates.code || ''),
                        user.uid
                    );
                }
                if (updates.tags !== undefined) {
                    encryptedUpdates.tags = await CryptoUtils.encrypt(
                        JSON.stringify(Array.isArray(updates.tags) ? updates.tags : []),
                        user.uid
                    );
                }
                if (updates.notes !== undefined) {
                    encryptedUpdates.notes = await CryptoUtils.encrypt(
                        String(updates.notes || ''),
                        user.uid
                    );
                }

                const nonEncryptedUpdates = {};
                if (updates.language !== undefined) nonEncryptedUpdates.language = updates.language;
                if (updates.name !== undefined) nonEncryptedUpdates.name = updates.name;
                if (updates.order !== undefined) nonEncryptedUpdates.order = updates.order;

                const finalUpdates = {
                    ...encryptedUpdates,
                    ...nonEncryptedUpdates,
                    updatedAt: Date.now(),
                };

                await setDoc(snippetRef, finalUpdates, { merge: true });
                pendingUpdates.current.delete(snippetId);
            } catch (error) {
                setError('Failed to save snippet');
            }
        }, DEBOUNCE_MS),
        [user?.uid]
    );
    
    /* ========================
     * loadSnippets - Loads snippets from Firestore and sets up a listener
     * ======================== */
    const loadSnippets = useCallback(async () => {
        if (!user?.uid || unsubscribeRef.current) {
            if (unsubscribeRef.current && snippets.length > 0) setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        const snippetsRef = collection(db, `users/${user.uid}/snippets`);
        const userSnippetsQuery = query(snippetsRef);

        unsubscribeRef.current = onSnapshot(
            userSnippetsQuery,
            async (snapshot) => {
                try {
                    const decryptedSnippets = await Promise.all(
                        snapshot.docs.map(async docSnapshot => {
                            const snippet = docSnapshot.data();
                            return {
                                id: docSnapshot.id,
                                code: await safeDecrypt(snippet.code, user.uid),
                                name: snippet.name || 'Untitled',
                                language: snippet.language || 'text',
                                tags: safeJSONParse(await safeDecrypt(snippet.tags, user.uid)),
                                notes: await safeDecrypt(snippet.notes, user.uid),
                                createdAt: snippet.createdAt || Date.now(),
                                updatedAt: snippet.updatedAt || Date.now(),
                                order: snippet.order !== undefined ? snippet.order : (snippet.createdAt || Date.now())
                            };
                        })
                    );
                    setSnippets(decryptedSnippets);
                    setError(null);
                } catch (err) {
                    setError('Failed to load snippets');
                    setSnippets([]);
                } finally {
                    setIsLoading(false);
                }
            },
            (err) => {
                setError('Failed to sync snippets');
                setSnippets([]);
                setIsLoading(false);
            }
        );
    }, [user?.uid, snippets.length]);

    /* ========================
     * unloadSnippets - Clears local snippets and unsubscribes from Firestore
     * ======================== */
    const unloadSnippets = useCallback(() => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        setSnippets([]);
        setIsLoading(false); 
        setError(null);
    }, []);

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [debouncedSave, user?.uid]);

    /* ========================
     * addSnippet - Adds a new snippet to Firestore
     * ======================== */
    const addSnippet = useCallback(async (code, language, name, tags = [], notes = '') => {
        if (!user?.uid) throw new Error('User must be authenticated');
        
        try {
            setIsLoading(true);
            const snippetId = `snippet_${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const snippetsRef = collection(db, `users/${user.uid}/snippets`);
            const snippetRef = doc(snippetsRef, snippetId);

            const currentTimestamp = Date.now();
            const sanitizedData = {
                code: String(code || ''),
                language: String(language || 'text'),
                name: String(name || 'Untitled'),
                tags: Array.isArray(tags) ? tags : [],
                notes: String(notes || ''),
                createdAt: currentTimestamp,
                updatedAt: currentTimestamp,
                order: currentTimestamp
            };

            const snippet = {
                code: await CryptoUtils.encrypt(sanitizedData.code, user.uid),
                language: sanitizedData.language,
                name: sanitizedData.name,
                tags: await CryptoUtils.encrypt(JSON.stringify(sanitizedData.tags), user.uid),
                notes: await CryptoUtils.encrypt(sanitizedData.notes, user.uid),
                createdAt: sanitizedData.createdAt,
                updatedAt: sanitizedData.updatedAt,
                order: sanitizedData.order
            };

            await setDoc(snippetRef, snippet);
            setIsLoading(false);
            return snippetId;
        } catch (error) {
            setError('Failed to add snippet');
            setIsLoading(false);
            throw error;
        }
    }, [user?.uid]);

    /* ========================
     * updateSnippet - Updates an existing snippet with optimistic UI and debounced save
     * ======================== */
    const updateSnippet = useCallback(async (snippetId, updates) => {
        if (!user?.uid) throw new Error('User must be authenticated');

        if (snippets.length > 0) {
            setSnippets(prev => prev.map(snippet => 
                snippet.id === snippetId 
                    ? { ...snippet, ...updates, updatedAt: Date.now() }
                    : snippet
            ));
        }

        const currentPending = pendingUpdates.current.get(snippetId) || {};
        pendingUpdates.current.set(snippetId, {
            ...currentPending,
            ...updates
        });

        debouncedSave(snippetId, pendingUpdates.current.get(snippetId));
    }, [user?.uid, debouncedSave, snippets.length]);

    /* ========================
     * deleteSnippet - Deletes a snippet from Firestore
     * ======================== */
    const deleteSnippet = useCallback(async (snippetId) => {
        if (!user?.uid) throw new Error('User must be authenticated');
        
        try {
            setIsLoading(true);
            pendingUpdates.current.delete(snippetId);

            const snippetsRef = collection(db, `users/${user.uid}/snippets`);
            const snippetRef = doc(snippetsRef, snippetId);
            await deleteDoc(snippetRef);
            setIsLoading(false);
        } catch (error) {
            setError('Failed to delete snippet');
            setIsLoading(false);
            throw error;
        }
    }, [user?.uid]);

    /* ========================
     * getAllTags - Gets all unique tags from the currently loaded snippets
     * ======================== */
    const getAllTags = useCallback(() => {
        const tagSet = new Set();
        snippets.forEach(snippet => {
            if (Array.isArray(snippet.tags)) {
                snippet.tags.forEach(tag => {
                    if (tag && typeof tag === 'string') {
                        tagSet.add(tag);
                    }
                });
            }
        });
        return Array.from(tagSet).sort();
    }, [snippets]);

    /* ========================
     * getFilteredSnippets - Filters and sorts snippets based on search and tags
     * ======================== */
    const getFilteredSnippets = useCallback(() => {
        let filteredSnippets = [...snippets].sort((a, b) => b.updatedAt - a.updatedAt);
        
        // Apply text search filter
        if (searchQuery) {
            const queryLower = searchQuery.toLowerCase();
            filteredSnippets = filteredSnippets.filter(snippet =>
                snippet.name?.toLowerCase().includes(queryLower) ||
                snippet.code?.toLowerCase().includes(queryLower) ||
                snippet.language?.toLowerCase().includes(queryLower) ||
                (Array.isArray(snippet.tags) && snippet.tags.some(tag => 
                    tag.toLowerCase().includes(queryLower))) ||
                snippet.notes?.toLowerCase().includes(queryLower)
            );
        }

        // Apply tag filter
        if (selectedTags.length > 0) {
            filteredSnippets = filteredSnippets.filter(snippet =>
                Array.isArray(snippet.tags) && 
                selectedTags.every(tag => snippet.tags.includes(tag))
            );
        }

        return filteredSnippets;
    }, [snippets, searchQuery, selectedTags]);

    /* ========================
     * reorderSnippets - Updates the order of snippets in Firestore
     * ======================== */
    const reorderSnippets = useCallback(async (newOrderedSnippets) => {
        if (!user?.uid) throw new Error('User must be authenticated');
        if (!Array.isArray(newOrderedSnippets)) return;

        try {
            setIsLoading(true);
            const updates = newOrderedSnippets.map((snippet, index) => 
                updateSnippet(snippet.id, { order: index })
            );
            await Promise.all(updates);
            if (snippets.length > 0) {
                const newSnippetsMap = new Map(newOrderedSnippets.map((s, i) => [s.id, { ...s, order: i }]));
                setSnippets(prevSnippets => 
                    prevSnippets
                        .map(ps => newSnippetsMap.has(ps.id) ? newSnippetsMap.get(ps.id) : ps)
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                );
            }
            setIsLoading(false);
        } catch (error) {
            setError('Failed to reorder snippets');
            setIsLoading(false);
            throw error;
        }
    }, [user?.uid, updateSnippet, snippets.length]);

    const value = {
        snippets: getFilteredSnippets(),
        rawSnippets: snippets,
        isLoading,
        error,
        addSnippet,
        deleteSnippet,
        updateSnippet,
        getAllTags,
        searchQuery,
        setSearchQuery,
        selectedTags,
        setSelectedTags,
        reorderSnippets,
        loadSnippets,
        unloadSnippets,
        isSnippetsLoaded: !!unsubscribeRef.current && snippets.length > 0,
    };

    return (
        <SnippetsContext.Provider value={value}>
            {children}
        </SnippetsContext.Provider>
    );
};

export default SnippetsManager;